import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import { useMediaDimensions } from "../../hooks/useMediaDimensions";
import { formatUrlMedia, getExtensionFile } from "../../utils/string";
import { VIDEO_FORMATS } from "../../utils/constants";

const DEFAULT_ASPECT_RATIO = 1;

const ContentApprovalMedia = ({
  url = "",
  mediaWidth = null,
  mediaHeight = null,
  sx = {},
}) => {
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  const formattedUrl = formatUrlMedia(url);
  const extension = getExtensionFile(formattedUrl);
  const isVideo = VIDEO_FORMATS.includes(extension);

  const hasBackendDimensions = Boolean(mediaWidth && mediaHeight);

  const shouldLoadDimensions = !hasBackendDimensions && Boolean(formattedUrl);

  const { aspectRatio: hookAspectRatio, loading: dimensionsLoading } =
    useMediaDimensions(shouldLoadDimensions ? formattedUrl : null);

  const aspectRatio = useMemo(() => {
    if (hasBackendDimensions) {
      return mediaWidth / mediaHeight;
    }

    if (!dimensionsLoading && hookAspectRatio) {
      return hookAspectRatio;
    }

    return DEFAULT_ASPECT_RATIO;
  }, [
    dimensionsLoading,
    hasBackendDimensions,
    hookAspectRatio,
    mediaHeight,
    mediaWidth,
  ]);

  const showLoader =
    !isMediaLoaded || (shouldLoadDimensions && dimensionsLoading);

  useEffect(() => {
    setIsMediaLoaded(false);
  }, [formattedUrl]);

  const onMediaReady = () => {
    setIsMediaLoaded(true);
  };

  const mediaStyle = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    opacity: isMediaLoaded ? 1 : 0,
    transition: "opacity 0.2s ease-out",
    borderRadius: sx?.borderRadius ?? 0,
  };

  const skeletonBorderRadius = sx?.borderRadius ?? 0;

  return (
    <>
      {showLoader ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Skeleton
            variant="rounded"
            animation={false}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              bgcolor: "background.paper",
              borderRadius: skeletonBorderRadius,
            }}
          />
          <CircularProgress size={32} />
        </Box>
      ) : null}

      <Box
        aria-busy={showLoader}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          aspectRatio,
          overflow: "hidden",
          flexShrink: 0,
          ...sx,
        }}
      >
        {isVideo ? (
          <Box
            component="video"
            src={formattedUrl}
            controls
            preload="metadata"
            onLoadedData={onMediaReady}
            onError={onMediaReady}
            sx={mediaStyle}
          />
        ) : (
          <Box
            component="img"
            src={formattedUrl}
            alt=""
            draggable={false}
            onLoad={onMediaReady}
            onError={onMediaReady}
            sx={mediaStyle}
          />
        )}
      </Box>
    </>
  );
};

export default ContentApprovalMedia;
